import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useParams } from 'react-router';
import { DetailPageSkeleton } from '../../../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { withinSection, type Crumb } from '../../../../../shared/components/section-trail';
import { AuditTimeline } from '../../../../../shared/features/history/components/AuditTimeline';
import { useHistory } from '../../../../../shared/features/history/hooks/use-history';
import { billingTabPath } from '../../../billing.paths';
import { ReceiptActionModal } from '../components/ReceiptActionModal';
import { ReceiptActions } from '../components/ReceiptActions';
import { ReceiptChain } from '../components/ReceiptChain';
import { ReceiptDetails } from '../components/ReceiptDetails';
import { ReceiptStatusTag } from '../components/ReceiptStatusTag';
import { useReceiptAction } from '../hooks/use-receipt-action';
import { useReceiptDetail } from '../hooks/use-receipt-detail';
import { useReceiptPermissions } from '../hooks/use-receipt-permissions';

const RECEIPTS_CRUMB: Crumb = { label: 'Receipts', to: billingTabPath('receipts') };

function ReceiptHistory({ receiptId }: { receiptId: string }) {
  return (
    <section className="mt-10">
      <h2 className="mb-4 text-base font-semibold text-fg">History</h2>
      <AuditTimeline state={useHistory('receipt', receiptId, true)} />
    </section>
  );
}

/**
 * One receipt. The fields are read-only (a mistake is a new row, never an edit); someone with `payment.record` can mark a cheque cleared
 * or bounced, or reverse a receipt. The history is the audit trail, so it shows only to someone who may read the audit log.
 */
export function ReceiptDetailPage() {
  const { id = '' } = useParams();
  const { state, reload } = useReceiptDetail(id);
  const { canRecord, canViewHistory } = useReceiptPermissions();
  const change = useReceiptAction(reload);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <DetailPageSkeleton label="Loading receipt" breadcrumbs={withinSection('billing', RECEIPTS_CRUMB)} tabs={0} />;
  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Receipt not found" description="This receipt doesn't exist, or the link is out of date." />;
  }
  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load this receipt" description="Something went wrong loading this receipt. Try reloading the page." />
    );
  }

  const { receipt } = state;

  return (
    <>
      <PageHeader
        breadcrumbs={withinSection('billing', RECEIPTS_CRUMB)}
        title={receipt.reference ?? 'No reference'}
        description={`Received ${receipt.receivedOn} from ${receipt.payer}`}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <ReceiptStatusTag status={receipt.status} />
            {canRecord && <ReceiptActions receipt={receipt} onAction={(action) => change.open(action, receipt)} />}
          </div>
        }
      />
      <ReceiptDetails receipt={receipt} />
      <ReceiptChain receipt={receipt} />
      {canRecord && (
        <ReceiptActionModal target={change.target} error={change.error} submitting={change.saving} onSubmit={change.submit} onClose={change.close} />
      )}
      {/* Keyed on the status so the timeline fetches again after an action adds an entry. */}
      {canViewHistory && <ReceiptHistory key={receipt.status} receiptId={receipt.id} />}
    </>
  );
}
